import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import { apiService } from '../services/api';
import { Project, BindingTable, TypeClassificationTable } from '../types';
import toast from 'react-hot-toast';
import LeftPanel from './LeftPanel';
import PdfViewer from './PdfViewer';
import TableEditor from './TableEditor';
import TypeClassificationEditor from './TypeClassificationEditor';

const Dashboard: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [bindingTable, setBindingTable] = useState<BindingTable | null>(null);
  const [vorTable, setVorTable] = useState<BindingTable | null>(null);
  const [typeClassificationTable, setTypeClassificationTable] = useState<TypeClassificationTable | null>(null);
  const [currentEditor, setCurrentEditor] = useState<'binding' | 'vor' | 'type' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadProjects();
  }, [navigate]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error('Ошибка загрузки проектов');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setBindingTable(null);
    setVorTable(null);
    setTypeClassificationTable(null);
    setCurrentEditor(null);
  };
  
  const handleTableSelect = async (project: Project) => {
    // Клик на конкретную таблицу привязки в левой панели
    if (!project.files.binding_table?.path) {
      toast.error("Для этого проекта нет таблицы привязки.");
      return;
    }
    setSelectedProject(project); // Устанавливаем как активный
    try {
      setIsLoading(true);
      const table = await apiService.getBindingTable(project.session_id, project.files.binding_table.path);
      setBindingTable(table);
      setVorTable(null);
      setTypeClassificationTable(null);
      setCurrentEditor('binding');
    } catch (error) {
      toast.error('Ошибка загрузки таблицы привязки');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeTableSelect = async (project: Project, typeTablePath: string) => {
    if (!typeTablePath) {
      toast.error("Для этого файла нет таблицы классификации.");
      return;
    }
    setSelectedProject(project);
    try {
      setIsLoading(true);
      const pdfStem = typeTablePath.split('/').pop()?.replace('_all_specifications_final_normalized_groupped_classified.xlsx', '') || '';
      const table = await apiService.getTypeTable(project.session_id, pdfStem);
      setTypeClassificationTable(table);
      setBindingTable(null);
      setVorTable(null);
      setCurrentEditor('type');
    } catch (error) {
      toast.error('Ошибка загрузки таблицы классификации типов');
    } finally {
      setIsLoading(false);
    }
  };


  const updateProjectDetails = async (sessionId: string) => {
    try {
      const updatedProjectData = await apiService.getProject(sessionId);
      setProjects(prevProjects =>
        prevProjects.map(p => (p.session_id === sessionId ? updatedProjectData : p))
      );
      if (selectedProject?.session_id === sessionId) {
        setSelectedProject(updatedProjectData);
      }
    } catch (error) {
      toast.error('Не удалось обновить детали проекта.');
    }
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;
    try {
      setIsLoading(true);
      const response = await apiService.uploadPdf(files);
      toast.success('Файл загружен! Начинается анализ...');
      
      const newProjectStub: Project = {
        session_id: response.session_id,
        project_name: files[0].name.replace(/\.pdf$/i, ''),
        pdf_filename: files[0].name,
        pdf_path: response.file_paths?.[0] || '',
        created_at: new Date().toISOString(),
        files: { 
          binding_table: null,
          type_classification_table: null,
          vor: null 
        }
      };

      setProjects(prev => [newProjectStub, ...prev]);
      setSelectedProject(newProjectStub);
      
      checkTaskStatus(response.task_id, response.session_id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки файлов');
    } finally {
      setIsLoading(false);
    }
  };

  const checkTaskStatus = (taskId: string, sessionId: string) => {
    const intervalId = setInterval(async () => {
      try {
        const status = await apiService.getTaskStatus(taskId);
        
        const isFinished = status.status === 'SUCCESS' || status.status === 'FAILURE';
        const isStageComplete = status.status === 'PENDING_CONFIRMATION' || status.status === 'AWAITING_BINDING_TABLE_CONFIRMATION';

        if (isFinished || isStageComplete) {
          if (status.status === 'SUCCESS') {
            toast.success('Обработка полностью завершена!');
          } else if (status.status === 'PENDING_CONFIRMATION') {
            toast.success('Первичная обработка завершена. Ожидается проверка типов.');
          } else if (status.status === 'AWAITING_BINDING_TABLE_CONFIRMATION') {
             toast.success('Таблица привязки создана. Ожидается проверка.');
          } else if (status.status === 'FAILURE') {
            toast.error(`Ошибка обработки: ${status.error || 'Неизвестная ошибка'}`);
          }
          
          clearInterval(intervalId);
          await updateProjectDetails(sessionId);

        }
      } catch (error) {
        toast.error('Ошибка проверки статуса задачи.');
        clearInterval(intervalId);
      }
    }, 5000);
  };

  const handleSaveTable = async (data: any[]) => {
    if (!selectedProject || !selectedProject.files.binding_table?.path) {
        toast.error("Не выбран проект или таблица для сохранения.");
        return;
    }
    try {
      setIsLoading(true);
      await apiService.saveBindingTable(selectedProject.session_id, {
        original_path: selectedProject.files.binding_table.path,
        data: data,
      });
      toast.success('Таблица успешно сохранена!');
      await updateProjectDetails(selectedProject.session_id);
    } catch (error) {
      toast.error('Ошибка сохранения таблицы');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTypeTable = async (data: any[]) => {
    if (!selectedProject || !typeClassificationTable) {
        toast.error("Не выбран проект или таблица для сохранения.");
        return;
    }
    try {
      setIsLoading(true);
      // Устанавливаем временный статус на клиенте
      setProjects(prev => prev.map(p => 
        p.session_id === selectedProject.session_id 
          ? { ...p, files: { ...p.files, status: 'GENERATING_BINDING_TABLE' } } 
          : p
      ));

      const pdfStem = typeClassificationTable.file_path.split('/').pop()?.replace('_types_for_frontend.xlsx', '') || '';
      const response = await apiService.saveTypeTable(selectedProject.session_id, pdfStem, data);

      toast.success('Таблица типов сохранена! Запускается финальная обработка...');
      setTypeClassificationTable(null);
      setCurrentEditor(null);
      
      checkTaskStatus(response.task_id, selectedProject.session_id);

    } catch (error) {
      toast.error('Ошибка сохранения таблицы типов');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateVor = async () => {
    if (!selectedProject || !selectedProject.files.binding_table?.corrected_path) {
      toast.error('Сначала нужно сохранить исправленную таблицу.');
      return;
    }
    try {
      setIsLoading(true);
      const response = await apiService.generateVor(selectedProject.files.binding_table.corrected_path);
      toast.success('Генерация ВОР начата!');
      checkTaskStatus(response.task_id, selectedProject.session_id);
    } catch (error) {
      toast.error('Ошибка генерации ВОР');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadVor = async () => {
    if (!selectedProject || !selectedProject.files.vor?.path) {
      toast.error("ВОР не найден для этого проекта.");
      return;
    }
    try {
      setIsLoading(true);
      const blob = await apiService.downloadFile(selectedProject.files.vor.path);
      
      // Создаем URL для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedProject.files.vor.name || 'vor.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('ВОР успешно скачан!');
    } catch (error) {
      toast.error('Ошибка скачивания ВОР');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVorSelect = async (project: Project) => {
    // Клик на ВОР для отображения в правой панели
    if (!project.files.vor?.path) {
      toast.error("ВОР не найден для этого проекта.");
      return;
    }
    setSelectedProject(project); // Устанавливаем как активный
    try {
      setIsLoading(true);
      const table = await apiService.getVorTable(project.session_id, project.files.vor.path);
      setVorTable(table);
      setBindingTable(null);
      setTypeClassificationTable(null);
      setCurrentEditor('vor');
    } catch (error) {
      toast.error('Ошибка загрузки ВОР');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    apiService.logout();
    navigate('/login');
  };

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Заголовок */}
      <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {/* <img className="h-8 w-8" src="/logo.svg" alt="App Logo" /> */}
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">DATATIM</h1>
              </div>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Выход
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Основная область */}
      <div className="flex-1 overflow-hidden">
        <Allotment>
          {/* Левая панель */}
          <Allotment.Pane preferredSize={320} minSize={250} maxSize={500}>
            <div className="h-full bg-white shadow-lg overflow-hidden">
              <LeftPanel
                projects={projects}
                selectedProject={selectedProject}
                onProjectSelect={handleProjectSelect}
                onTableSelect={handleTableSelect}
                onTypeTableSelect={handleTypeTableSelect}
                onVorSelect={handleVorSelect}
                onFileUpload={handleFileUpload}
                isLoading={isLoading}
              />
            </div>
          </Allotment.Pane>

          {/* Правая часть - PDF Viewer и Table Editor */}
          <Allotment.Pane>
            <Allotment>
              {/* Средняя панель - PDF Viewer */}
              <Allotment.Pane preferredSize="50%" minSize={300}>
                <div className="h-full bg-gray-50 overflow-hidden">
                  <PdfViewer
                    pdfPath={selectedProject?.pdf_path || null}
                    projectName={selectedProject?.project_name || null}
                    sessionId={selectedProject?.session_id}
                    pdfFilename={selectedProject?.pdf_filename}
                  />
                </div>
              </Allotment.Pane>

              {/* Правая панель - Table Editor */}
              <Allotment.Pane preferredSize="50%" minSize={300}>
                <div className="h-full flex flex-col bg-white shadow-lg">
                  {currentEditor === 'binding' && (
                    <TableEditor
                      key={selectedProject?.session_id + '-binding'}
                      bindingTable={bindingTable}
                      vorTable={vorTable}
                      viewMode={'binding'}
                      onSaveTable={handleSaveTable}
                      onGenerateVor={handleGenerateVor}
                      onDownloadVor={handleDownloadVor}
                      isCorrected={selectedProject?.files.binding_table?.is_corrected || false}
                      vorPath={selectedProject?.files.vor?.path || null}
                      isLoading={isLoading}
                    />
                  )}
                  {currentEditor === 'vor' && (
                     <TableEditor
                      key={selectedProject?.session_id + '-vor'}
                      bindingTable={bindingTable}
                      vorTable={vorTable}
                      viewMode={'vor'}
                      onDownloadVor={handleDownloadVor}
                      vorPath={selectedProject?.files.vor?.path || null}
                      isLoading={isLoading}
                    />
                  )}
                   {currentEditor === 'type' && typeClassificationTable && (
                    <TypeClassificationEditor
                      key={selectedProject?.session_id + '-type'}
                      tableData={typeClassificationTable}
                      onSave={handleSaveTypeTable}
                      isLoading={isLoading}
                    />
                  )}
                </div>
              </Allotment.Pane>
            </Allotment>
          </Allotment.Pane>
        </Allotment>
      </div>
    </div>
  );
};

export default Dashboard; 